/**
 * /timecode-calculator (SEO_BRIEF_11 section 2.1): timecodes added and subtracted, and converted
 * between timecode, total frames, seconds and SRT time, at the nine frame rates the brief lists.
 * Pure functions; the page (web/static/base/src/pages/calculator.ts) reads the boxes and writes
 * the answers as the visitor types.
 *
 * EVERYTHING GOES THROUGH A FRAME COUNT. A timecode is a label for one frame; adding two is adding
 * their frame counts and labelling the sum. The rates are exact (contracts/cue-model.ts `FPS`,
 * 24000/1001 rather than 23.976), so 01:00:00:00 at 23.976 is 86,400 frames and 3,603.6 seconds of
 * real time, as an editor measures it.
 *
 * DROP-FRAME (29.97 only, as the brief lists it) keeps the label close to the clock by skipping
 * the frame NUMBERS 00 and 01 at the start of every minute except every tenth; no picture is
 * dropped. The arithmetic is the SCC writer's (src/core/time/timestamp.ts `sccTimecodeToFrames`,
 * `formatSccTimecode`), the one place the site already does it, and a label drop-frame skips
 * (00:01:00;00) is refused here rather than read as a neighbouring frame. A drop-frame timecode is
 * written with `;` before the frames, as editors show it; any of `:` `;` `.` `,` is read.
 *
 * A TIMECODE AS TYPED: HH:MM:SS:FF, or fewer fields from the right (SS:FF, MM:SS:FF), or digits
 * alone, read from the right as editors do: 1000 is 00:00:10:00. Digits and a dot are what a
 * phone's number pad has, so 1.0.0.0 works too.
 */

import { FPS, type Framerate } from '../../contracts/cue-model.ts';
import { formatSccTimecode, sccTimecodeToFrames } from '../core/time/timestamp.ts';

export interface TimecodeRate {
  /** The value the page's frame-rate picker sends. */
  readonly id: string;
  readonly fps: Framerate;
  /** Drop-frame labels: 29.97 drop-frame only. */
  readonly drop: boolean;
}

/** The nine rates of section 2.1, in the picker's order. */
export const TIMECODE_RATES = [
  { id: '23.976', fps: FPS.F23_976, drop: false },
  { id: '24', fps: FPS.F24, drop: false },
  { id: '25', fps: FPS.F25, drop: false },
  { id: '29.97-df', fps: FPS.F29_97, drop: true },
  { id: '29.97', fps: FPS.F29_97, drop: false },
  { id: '30', fps: FPS.F30, drop: false },
  { id: '50', fps: FPS.F50, drop: false },
  { id: '59.94', fps: FPS.F59_94, drop: false },
  { id: '60', fps: FPS.F60, drop: false },
] as const satisfies readonly TimecodeRate[];

export type RateId = (typeof TIMECODE_RATES)[number]['id'];

/** The rate a page opens on. */
export const DEFAULT_RATE: RateId = '24';

/** The most hours a timecode may carry. */
export const MAX_HOURS = 999;

/** The rate a picker value names, or null. */
export function rateById(id: string): TimecodeRate | null {
  return TIMECODE_RATES.find((r) => r.id === id) ?? null;
}

/** Frame numbers in a labelled second: 24 at 23.976, 30 at 29.97. */
export function nominalFps(rate: TimecodeRate): number {
  return Math.round(rate.fps.num / rate.fps.den);
}

/** Frame numbers a drop-frame label skips at the start of a minute: 2 at 29.97, else 0. */
export function droppedPerMinute(rate: TimecodeRate): number {
  return rate.drop ? Math.round(nominalFps(rate) / 15) : 0;
}

/**
 * Why a box could not be read:
 *   empty    nothing typed (the page shows no answer and no message)
 *   format   not the shape of the box's kind of value
 *   range    minutes or seconds of 60 or more
 *   frames   a frame number the rate does not have (24 or more at 24 fps)
 *   drop     a drop-frame label that is skipped (00:01:00;00)
 */
export type Problem = 'empty' | 'format' | 'range' | 'frames' | 'drop';

export type Read<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly problem: Problem };

const ok = <T>(value: T): Read<T> => ({ ok: true, value });
const no = <T>(problem: Problem): Read<T> => ({ ok: false, problem });

/** A timecode as typed (the shapes at the top of this file), as a frame count at `rate`. */
export function parseTimecode(input: string, rate: TimecodeRate): Read<number> {
  const s = input.trim();
  if (s === '') return no('empty');
  let fields: number[];
  if (/^\d+$/.test(s)) {
    if (s.length > 8) return no('format');
    const d = s.padStart(8, '0');
    fields = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 6), d.slice(6, 8)].map(Number);
  } else {
    const parts = s.split(/[:;.,]/);
    if (parts.length < 2 || parts.length > 4) return no('format');
    const hoursAt = parts.length === 4 ? 0 : -1;
    for (const [i, part] of parts.entries()) {
      const width = i === hoursAt ? 3 : 2;
      if (!new RegExp(`^\\d{1,${width}}$`).test(part)) return no('format');
    }
    fields = parts.map(Number);
    while (fields.length < 4) fields.unshift(0);
  }
  const [hh = 0, mm = 0, ss = 0, ff = 0] = fields;
  if (hh > MAX_HOURS) return no('format');
  if (mm > 59 || ss > 59) return no('range');
  if (ff >= nominalFps(rate)) return no('frames');
  if (rate.drop && ss === 0 && ff < droppedPerMinute(rate) && mm % 10 !== 0) return no('drop');
  return ok(sccTimecodeToFrames(hh, mm, ss, ff, rate.fps, rate.drop));
}

/** A frame count as a timecode at `rate`, `;` before the frames in drop-frame, `-` when negative. */
export function formatTimecode(frames: number, rate: TimecodeRate): string {
  return formatSccTimecode(Math.trunc(frames), rate.fps, rate.drop);
}

/** A frame count as real seconds at `rate` (not rounded). */
export function framesToSeconds(frames: number, rate: TimecodeRate): number {
  return (frames * rate.fps.den) / rate.fps.num;
}

/** Real seconds as the nearest frame count at `rate`. */
export function secondsToFrames(seconds: number, rate: TimecodeRate): number {
  return Math.round((seconds * rate.fps.num) / rate.fps.den);
}

/** A whole number of frames as typed. */
export function parseFrames(input: string): Read<number> {
  const s = input.trim();
  if (s === '') return no('empty');
  if (!/^\d{1,9}$/.test(s)) return no('format');
  return ok(Number(s));
}

/** Seconds as typed, with a dot or a comma for the decimals. */
export function parseSeconds(input: string): Read<number> {
  const s = input.trim().replace(',', '.');
  if (s === '') return no('empty');
  if (!/^\d+(?:\.\d+)?$|^\.\d+$/.test(s)) return no('format');
  const n = Number(s);
  return Number.isFinite(n) && n <= (MAX_HOURS + 1) * 3600 ? ok(n) : no('format');
}

/** Seconds as the page shows them: up to three decimals, no trailing zeros ("3603.6"). */
export function formatSeconds(seconds: number): string {
  return String(Math.round(seconds * 1000) / 1000);
}

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

/** Seconds as SRT writes a time, HH:MM:SS,mmm, to the nearest millisecond. */
export function formatSrtTime(seconds: number): string {
  const ms = Math.max(0, Math.round(seconds * 1000));
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor(ms / 60_000) % 60;
  const s = Math.floor(ms / 1000) % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(ms % 1000, 3)}`;
}

const SRT_TIME = /^(?:(\d{1,3}):)?(\d{1,2}):(\d{1,2})(?:[,.](\d{1,3}))?$/;

/**
 * An SRT time as typed, in seconds: HH:MM:SS,mmm, with the hours or the milliseconds left off if
 * the visitor likes and a dot for the comma. The milliseconds are a fraction, so ",5" is 500 ms.
 */
export function parseSrtTime(input: string): Read<number> {
  const s = input.trim();
  if (s === '') return no('empty');
  const m = SRT_TIME.exec(s);
  if (!m) return no('format');
  const [, h = '0', mm = '0', ss = '0', frac = ''] = m;
  if (Number(mm) > 59 || Number(ss) > 59) return no('range');
  const ms = Number(frac.padEnd(3, '0'));
  return ok(Number(h) * 3600 + Number(mm) * 60 + Number(ss) + ms / 1000);
}

/** The four boxes of the converter, by the name the page gives them. */
export type Unit = 'timecode' | 'frames' | 'seconds' | 'srt';
export const UNITS: readonly Unit[] = ['timecode', 'frames', 'seconds', 'srt'];

export interface Converted {
  readonly timecode: string;
  readonly frames: number;
  readonly seconds: string;
  readonly srt: string;
}

/**
 * One box of the converter, read and written as all four. A timecode or a frame count is a frame,
 * and its seconds and SRT time are that frame's. Seconds or an SRT time are a moment: the
 * timecode and the frames are the nearest frame to it, and the other of the two keeps the moment
 * itself, so 00:00:01,510 is 1.51 seconds, not the 1.5 its nearest frame at 24 fps starts on.
 */
export function convert(unit: Unit, input: string, rate: TimecodeRate): Read<Converted> {
  let frames: number;
  let seconds: number;
  if (unit === 'timecode' || unit === 'frames') {
    const read = unit === 'timecode' ? parseTimecode(input, rate) : parseFrames(input);
    if (!read.ok) return read;
    frames = read.value;
    seconds = framesToSeconds(frames, rate);
  } else {
    const read = unit === 'seconds' ? parseSeconds(input) : parseSrtTime(input);
    if (!read.ok) return read;
    seconds = read.value;
    frames = secondsToFrames(seconds, rate);
  }
  return ok({
    timecode: formatTimecode(frames, rate),
    frames,
    seconds: formatSeconds(seconds),
    srt: formatSrtTime(seconds),
  });
}

export type Operation = 'add' | 'subtract';

export interface Sum {
  readonly a: Read<number>;
  readonly b: Read<number>;
  /** The answer, or null while either timecode cannot be read. */
  readonly result: { readonly frames: number; readonly timecode: string } | null;
}

/** `a` plus or minus `b` at `rate`. A difference below zero is written with a minus sign. */
export function calculate(a: string, op: Operation, b: string, rate: TimecodeRate): Sum {
  const ra = parseTimecode(a, rate);
  const rb = parseTimecode(b, rate);
  if (!ra.ok || !rb.ok) return { a: ra, b: rb, result: null };
  const frames = op === 'add' ? ra.value + rb.value : ra.value - rb.value;
  return { a: ra, b: rb, result: { frames, timecode: formatTimecode(frames, rate) } };
}
